import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { InputNumber, Select, Icon, Popover } from 'antd';
import {
  cancellablePromise,
  useCancellablePromise,
} from '../ComplexDocumentDesign/cancellablePromise';
import { useDocumentContext } from '../../context/complexDocumentContext';
import { useArtboardContext, CONTEXT_KEYS } from '../../context/complexArtboardContext';
import ColorPicker from '../../artboard/ColorPicker';
import { measurementConversion } from '../../utils/javascript';
import { COMPLEX_PAGE_OPTIONS } from '../ComplexDocumentDesign/translationValues';
import {
  LayoutContainer,
  PageLayoutInputLabel,
  BackgroundColorBox,
  PageBackgroundPicker,
} from '../ComplexDocumentDesign/styles';
import { COMPLEX_EMAIL_LAYOUT_SETTINGS } from './translationValues';
import { FontFamilyInput, SizeButton, SizeContainer } from '../SidebarMenu/styles';
import FontSideBar from '../ComplexDocumentDesign/FontSideBar';
import { useDefaultStyleContext } from '../../context/complexDefaultStyleContext';
import { ButtonGroup } from 'react-rainbow-components';
import { fontSizes } from '../ComplexDocumentDesign/TextBlockToolbar';

const { Option } = Select;

const getMarginName = marginIndex => {
  switch (marginIndex) {
    case 0:
      return 'marginTop';
    case 1:
      return 'marginRight';
    case 2:
      return 'marginBottom';
    case 3:
      return 'marginLeft';
    default:
      return null;
  }
};

const NAMESPACES = ['COMPLEX_PAGE_OPTIONS'];

const ComplexEmailLayoutSettings = () => {
  const currentPageSelector = useCallback(
    store => (store[CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE] ? store[CONTEXT_KEYS.CURRENT_PAGE] : ''),
    [],
  );
  const [currentPageUUID, updateArtboard] = useArtboardContext(
    currentPageSelector,
    `ComplexPageLayoutSettings`,
  );

  const editableSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN], []);
  const [editable] = useArtboardContext(editableSelector);

  const visibilitySelector = useCallback(store => store[CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE], []);
  const [isVisible] = useArtboardContext(visibilitySelector);

  const topMargin = useRef(null);
  const rightMargin = useRef(null);
  const bottomMargin = useRef(null);
  const leftMargin = useRef(null);
  const changes = useRef({});
  const { t } = useTranslation(NAMESPACES);

  const {
    addCancellablePromise,
    removePendingPromise,
    clearPendingPromises,
  } = useCancellablePromise();

  const customSelector = useCallback(
    (store, previousState) => {
      return store[currentPageUUID];
    },
    [currentPageUUID],
  );

  const [pageDetails, updatePageMethods] = useDocumentContext(
    customSelector,
    `ComplexPageLayoutSettings`,
  );
  const [showPicker, toggleShowPicker] = useState(false);
  const [picker, setPicker] = useState(false);
  const [pageDetailBySize, setPageDetailsBySize] = useState({});
  const [isCustomSize, toggleCustomSize] = useState(false);
  const [sizeIn, setSizeIn] = useState('px');
  const [fontListVisible, toggleFontList] = useState(false);

  const getCurrentDesign = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [CurrentDesign] = useArtboardContext(getCurrentDesign);

  const getDefaultStyleSelector = useCallback(
    store => ({ fontFamily: store.fontFamily, fontSize: store.fontSize }),
    [],
  );
  const [defaultStyle] = useDefaultStyleContext(getDefaultStyleSelector);

  const [, methods] = useDocumentContext(() => null, 'ComlpexSettingsTab');
  const inputRef = useRef();

  useEffect(() => {
    if (pageDetails && pageDetails?.size === 'custom') {
      toggleCustomSize(true);
    } else {
      toggleCustomSize(false);
    }
  }, [pageDetails]);

  useEffect(() => {
    let pageSize = {
      height: 1123,
      heightInMM: 297,
      margin: [0, 0, 0, 0],
      width: 794,
      widthInMM: 210,
    };
    if (pageDetails) {
      if (sizeIn === 'mm') {
        pageSize = {
          height: pageDetails.heightInMM,
          width: pageDetails.widthInMM,
          margin: [
            measurementConversion(pageDetails.margin[0], 'mm'),
            measurementConversion(pageDetails.margin[1], 'mm'),
            measurementConversion(pageDetails.margin[2], 'mm'),
            measurementConversion(pageDetails.margin[3], 'mm'),
          ],
        };
      } else {
        pageSize = {
          height: pageDetails.height,
          width: pageDetails.width,
          margin: pageDetails.margin,
        };
      }
      setPageDetailsBySize(pageSize);
    } else {
      setPageDetailsBySize(pageSize);
    }
  }, [pageDetails, sizeIn]);

  const updateHeightWidth = useCallback(
    async (value, size) => {
      if (value > 800) {
        // TODO : Show toast message on max width restriction
        return;
      }
      clearPendingPromises();
      const cancellablePromise1 = cancellablePromise(
        new Promise(resolve => setTimeout(resolve, 1000)),
      );
      changes.current = Object.assign({}, changes.current, {
        uuid: currentPageUUID,
        [size]: value,
      });
      addCancellablePromise(cancellablePromise1);

      try {
        await cancellablePromise1.promise;
        let convertedValues = {};
        let resizeSuccess;
        if (size === 'height') {
          if (sizeIn === 'px') {
            convertedValues = {
              heightInPX: value,
              heightInMM: measurementConversion(value, 'mm'),
            };
          } else {
            convertedValues = {
              heightInPX: measurementConversion(value, 'px'),
              heightInMM: value,
            };
          }
          resizeSuccess = updatePageMethods.setFlowSettings('setPageHeight', {
            uuid: currentPageUUID,
            size: 'custom',
            ...convertedValues,
            orientation: 'portrait',
          });
        } else {
          if (sizeIn === 'px') {
            convertedValues = {
              widthInPX: value,
              widthInMM: measurementConversion(value, 'mm'),
            };
          } else {
            convertedValues = {
              widthInPX: measurementConversion(value, 'px'),
              widthInMM: value,
            };
          }
          resizeSuccess = updatePageMethods.setFlowSettings('setPageWidth', {
            uuid: currentPageUUID,
            size: 'custom',
            ...convertedValues,
            orientation: 'portrait',
          });
        }
      } catch (error) {
        removePendingPromise(cancellablePromise1);
      }
    },
    [currentPageUUID, pageDetails, sizeIn],
  );

  const updateMargin = useCallback(
    async (value, marginIndex) => {
      clearPendingPromises();
      let convertedValue;
      if (sizeIn === 'px') {
        convertedValue = value;
      } else {
        convertedValue = measurementConversion(value, 'px');
      }
      const cancellablePromise1 = cancellablePromise(
        new Promise(resolve => setTimeout(resolve, 1000)),
      );
      let marginName = getMarginName(marginIndex);
      changes.current = Object.assign({}, changes.current, {
        uuid: currentPageUUID,
        [marginName]: convertedValue,
      });
      addCancellablePromise(cancellablePromise1);

      try {
        await cancellablePromise1.promise;
        const resizeSuccess = updatePageMethods.setFlowSettings('setPageMargin', changes.current);
      } catch (error) {
        removePendingPromise(cancellablePromise1);
      }
    },
    [currentPageUUID, sizeIn],
  );

  const onSizeChange = useCallback(newFontSize => {
    if (!isNaN(newFontSize) && newFontSize > 0) {
      const designType = CurrentDesign === 'document' ? 'flow' : CurrentDesign;
      methods.updatefontFamilyAndSize(
        designType,
        newFontSize,
        defaultStyle.fontSize,
        'fontSize',
        'update',
      );
    }
  }, []);

  const changeFontFamily = useCallback(newFontFamily => {
    const designType = CurrentDesign === 'document' ? 'flow' : CurrentDesign;
    methods.updatefontFamilyAndSize(
      designType,
      newFontFamily,
      defaultStyle.fontFamily,
      'fontFamily',
      'update',
    );
  }, []);

  const onFontToogle = useCallback(() => {
    toggleFontList(prevState => !prevState);
  }, []);

  const handleKeypress = e => {
    e.stopPropagation();
    if (['Enter', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
      e.preventDefault();
      const value = parseFloat(inputRef.current.innerHTML.trim());
      if (!isNaN(value) && value >= 1 && value <= 400) {
        onSizeChange(value);
      }
    }
  };

  const renderSize = () => {
    return (
      <span>
        <ButtonGroup style={{ border: '1px solid #d9d9d9', borderRadius: 5, padding: '0px 1px' }}>
          <SizeButton
            comboBox={1}
            isMinus={1}
            onClick={() => {
              if (!isNaN(defaultStyle.fontSize)) {
                onSizeChange(defaultStyle.fontSize - 1);
              }
            }}
            radiusLeft={true}
            icon="minus"
          />
          <Popover
            content={
              <SizeContainer>
                <ul>
                  {fontSizes.map(data => (
                    <li
                      key={data}
                      onClick={e => {
                        onSizeChange(data?.replaceAll('px', ''));
                      }}
                    >
                      {data}
                    </li>
                  ))}
                </ul>
              </SizeContainer>
            }
            title={null}
            trigger="click"
          >
            <div
              ref={inputRef}
              contentEditable={true}
              suppressContentEditableWarning={true}
              className="size-ref ql-size"
              style={{
                width: '50px',
                color: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#cdcdcd',
              }}
              onKeyPress={handleKeypress}
              onBlur={e => {
                const value = parseFloat(inputRef.current.innerHTML.trim());
                if (
                  value !== defaultStyle.fontSize &&
                  !isNaN(value) &&
                  value >= 1 &&
                  value <= 400
                ) {
                  onSizeChange(value);
                }
              }}
            >
              {!isNaN(defaultStyle.fontSize) && defaultStyle.fontSize ? defaultStyle.fontSize : ' '}
            </div>
          </Popover>
          <SizeButton
            comboBox={1}
            isMinus={1}
            onClick={() => {
              if (!isNaN(defaultStyle.fontSize)) {
                onSizeChange(defaultStyle.fontSize + 1);
              }
            }}
            radiusLeft={false}
            icon="plus"
          />
        </ButtonGroup>
      </span>
    );
  };

  const onBackgroundColorClick = useCallback(() => {
    toggleShowPicker(true);
    setPicker('background');
  }, []);

  const onColorClick = useCallback(() => {
    toggleShowPicker(true);
    setPicker('color');
  }, []);

  const onColorPick = useCallback(
    color => {
      if (color && color.hex) {
        updatePageMethods.setFlowSettings('setPageColor', {
          color: color.hex,
          uuid: currentPageUUID,
        });
      }
    },
    [currentPageUUID],
  );

  const onBackgroundColorPick = useCallback(
    color => {
      if (color && color.hex) {
        updatePageMethods.setFlowSettings('setPageBackground', {
          backgroundColor: color.hex,
          uuid: currentPageUUID,
        });
      }
    },
    [currentPageUUID],
  );

  if (!isVisible || pageDetails === undefined || pageDetails === null) return <div />;

  return (
    <>
      {ReactDOM.createPortal(
        <LayoutContainer isPageSettings={1} onKeyDown={e => e.stopPropagation()}>
          <div className="title">
            {t(
              COMPLEX_EMAIL_LAYOUT_SETTINGS.PAGE_SETTING_HEADER.key,
              COMPLEX_EMAIL_LAYOUT_SETTINGS.PAGE_SETTING_HEADER.value,
            )}
            <div className="size-container">
              <PageLayoutInputLabel>
                {' '}
                {t(
                  COMPLEX_PAGE_OPTIONS.CHANGE_SIZE_LABEL.key,
                  COMPLEX_PAGE_OPTIONS.CHANGE_SIZE_LABEL.value,
                )}{' '}
              </PageLayoutInputLabel>
              <Select value={sizeIn} style={{ height: '38px' }} disabled={true} onChange={val => setSizeIn(val)}>
                <Option value="px"> px </Option>
                <Option value="mm"> mm </Option>
              </Select>
            </div>
          </div>
          <div className="properties-container">
            <div className="grid" style={{ alignItems: 'center' }}>
              <div className="input-container">
                <PageLayoutInputLabel>
                  {' '}
                  {/*t(
                    COMPLEX_PAGE_OPTIONS.PAGE_WIDTH_LABEL.key,
                    COMPLEX_PAGE_OPTIONS.PAGE_WIDTH_LABEL.value,
                  )*/}
                  {'Maximum width of content'}
                  {' '}
                </PageLayoutInputLabel>
                <InputNumber
                  disabled={!editable || !isCustomSize}
                  ref={bottomMargin}
                  type="number"
                  size="large"
                  value={pageDetailBySize?.width}
                  onChange={e => updateHeightWidth(parseInt(e), 'width')}
                  max={800}
                />
              </div>
            </div>
            <div className="grid" style={{ alignItems: 'center' }}>
              <div className="input-container">
              <PageLayoutInputLabel>
                {'Email Background'}
              </PageLayoutInputLabel>
              <BackgroundColorBox
                style={{ backgroundColor: pageDetails?.backgroundColor, width: 125 }}
                onClick={onBackgroundColorClick}
                disabled={!editable}
              >
                {showPicker && picker === 'background' && (
                  <PageBackgroundPicker>
                    <ColorPicker
                      onChange={onBackgroundColorPick}
                      onBlur={() => toggleShowPicker(false)}
                      color={pageDetails?.backgroundColor}
                      qlEditor={true}
                    />
                  </PageBackgroundPicker>
                )}
              </BackgroundColorBox>
              </div>
              <div className="input-container">
              <PageLayoutInputLabel>
                {'Content background'}
              </PageLayoutInputLabel>
              <BackgroundColorBox
                style={{ backgroundColor: pageDetails?.color, width: 125 }}
                onClick={onColorClick}
                disabled={!editable}
              >
                {showPicker && picker === 'color' && (
                  <PageBackgroundPicker style={{ left: -70 }}>
                    <ColorPicker
                      onChange={onColorPick}
                      onBlur={() => toggleShowPicker(false)}
                      color={pageDetails?.color}
                      qlEditor={true}
                    />
                  </PageBackgroundPicker>
                )}
              </BackgroundColorBox>
              </div>
            </div>
            <div className="grid">
              <div className="input-container">
                <PageLayoutInputLabel>
                  {' '}
                  {t(
                    COMPLEX_PAGE_OPTIONS.PAGE_TOP_MARGIN_LABEL.key,
                    COMPLEX_PAGE_OPTIONS.PAGE_TOP_MARGIN_LABEL.value,
                  )}{' '}
                </PageLayoutInputLabel>
                <InputNumber
                  ref={topMargin}
                  type="number"
                  size="large"
                  value={pageDetailBySize?.margin[0]}
                  onChange={value => updateMargin(value, 0)}
                  min={0}
                  max={10000}
                  disabled={!editable}
                />
              </div>
              <div className="input-container">
                <PageLayoutInputLabel>
                  {' '}
                  {t(
                    COMPLEX_PAGE_OPTIONS.PAGE_BOTTOM_MARGIN_LABEL.key,
                    COMPLEX_PAGE_OPTIONS.PAGE_BOTTOM_MARGIN_LABEL.value,
                  )}{' '}
                </PageLayoutInputLabel>
                <InputNumber
                  ref={bottomMargin}
                  type="number"
                  size="large"
                  min={0}
                  max={10000}
                  value={pageDetailBySize?.margin[2]}
                  onChange={value => updateMargin(value, 2)}
                  disabled={!editable}
                />
              </div>
            </div>
            <div className="grid">
              <div className="input-container">
                <PageLayoutInputLabel>
                  {' '}
                  {t(
                    COMPLEX_PAGE_OPTIONS.PAGE_LEFT_MARGIN_LABEL.key,
                    COMPLEX_PAGE_OPTIONS.PAGE_LEFT_MARGIN_LABEL.value,
                  )}{' '}
                </PageLayoutInputLabel>
                <InputNumber
                  ref={leftMargin}
                  type="number"
                  size="large"
                  value={pageDetailBySize?.margin[3]}
                  min={0}
                  max={10000}
                  onChange={value => updateMargin(value, 3)}
                  disabled={!editable}
                />
              </div>
              <div className="input-container">
                <PageLayoutInputLabel>
                  {' '}
                  {t(
                    COMPLEX_PAGE_OPTIONS.PAGE_RIGHT_MARGIN_LABEL.key,
                    COMPLEX_PAGE_OPTIONS.PAGE_RIGHT_MARGIN_LABEL.value,
                  )}{' '}
                </PageLayoutInputLabel>
                <InputNumber
                  ref={rightMargin}
                  type="number"
                  size="large"
                  min={0}
                  max={10000}
                  value={pageDetailBySize?.margin[1]}
                  onChange={value => updateMargin(value, 1)}
                  disabled={!editable}
                />
              </div>
            </div>
            <div>
              <PageLayoutInputLabel>
                {t(
                  COMPLEX_EMAIL_LAYOUT_SETTINGS.FONT_FAMILY.key,
                  COMPLEX_EMAIL_LAYOUT_SETTINGS.FONT_FAMILY.value,
                )}
              </PageLayoutInputLabel>
              <FontFamilyInput complexEmailDesign>
                <div onClick={() => toggleFontList(true)} className="input">
                  <span>{defaultStyle.fontFamily}</span>
                </div>
              </FontFamilyInput>

              <PageLayoutInputLabel>
                {t(
                  COMPLEX_EMAIL_LAYOUT_SETTINGS.FONT_SIZE.key,
                  COMPLEX_EMAIL_LAYOUT_SETTINGS.FONT_SIZE.value,
                )}
              </PageLayoutInputLabel>

              <div style={{ margin: '5px 0px 0px' }}>{renderSize()}</div>

              {fontListVisible && (
                <FontSideBar
                  itemUUID={undefined}
                  editor={undefined}
                  setFontChange={changeFontFamily}
                  fontFamily={defaultStyle.fontFamily}
                  isToolBarVisible={fontListVisible}
                  closeFontList={onFontToogle}
                  isAbsolute={true}
                  isComplexEmailDesign={true}
                />
              )}
            </div>
          </div>
        </LayoutContainer>,
        document.getElementById('sidebar-absolute-container'),
      )}
      {ReactDOM.createPortal(
        <div
          className="layoutbtn-container"
          onClick={() => {
            updateArtboard({
              [CONTEXT_KEYS.CURRENT_PAGE]: '',
              [CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE]: false,
            });
          }}
        >
          <Icon type={'close'} />
        </div>,
        document.getElementById('sidebar-absolute-close'),
      )}
    </>
  );
};

export default ComplexEmailLayoutSettings;
